
import React from 'react';
import QRCode from 'qrcode';

interface QRCodeImageComposerProps {
  imageUrl: string;
  qrCodeData: string;
  eventName?: string;
  eventDate?: string;
  eventTime?: string;
  eventLocation?: string;
  hostName?: string;
  eventType?: 'voting' | 'rsvp';
  deadline?: string;
  description?: string;
  votingOptions?: Array<{ type: string; value: string }>;
  onCompositeReady: (compositeImageUrl: string) => void;
}

export const QRCodeImageComposer: React.FC<QRCodeImageComposerProps> = ({
  imageUrl,
  qrCodeData,
  eventName,
  eventDate,
  eventTime,
  eventLocation,
  hostName,
  eventType,
  deadline,
  description,
  votingOptions,
  onCompositeReady
}) => {

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr?: string) => {
    if (!timeStr) return '';
    try {
      const [hours, minutes] = timeStr.split(':');
      const date = new Date();
      date.setHours(parseInt(hours), parseInt(minutes));
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } catch {
      return timeStr;
    }
  };

  const validateUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      const isValidPath = urlObj.pathname.includes('/vote/');
      const hasShareableLink = urlObj.pathname.split('/vote/')[1]?.length > 5;
      console.log('URL validation:', { url, isValidPath, hasShareableLink });
      return isValidPath && hasShareableLink;
    } catch (error) {
      console.error('URL validation error:', error);
      return false;
    }
  };

  const composeImageWithQR = async () => {
    try {
      console.log('=== QR CODE COMPOSITION START ===');
      console.log('Starting QR code composition with:', { 
        imageUrl, 
        qrCodeData,
        eventName,
        eventDate,
        eventTime,
        eventLocation,
        eventType,
        votingOptions
      });
      
      // Validate the QR code URL
      if (!validateUrl(qrCodeData)) {
        console.error('Invalid QR code URL:', qrCodeData);
        throw new Error('Invalid QR code URL');
      }

      // Create a canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');

      // Load the main image
      const mainImage = new Image();
      mainImage.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        mainImage.onload = () => {
          console.log('Main image loaded successfully:', mainImage.width, 'x', mainImage.height);
          resolve(true);
        };
        mainImage.onerror = (error) => {
          console.error('Failed to load main image:', error);
          reject(error);
        };
        mainImage.src = imageUrl;
      });

      // Set canvas size to match the main image
      canvas.width = mainImage.width;
      canvas.height = mainImage.height;
      console.log('Canvas dimensions set to:', canvas.width, 'x', canvas.height);

      // Draw the main image
      ctx.drawImage(mainImage, 0, 0);
      console.log('Main image drawn to canvas');

      // Generate QR code with optimized settings for better scannability
      console.log('Generating QR code for URL:', qrCodeData);
      const qrCodeDataUrl = await QRCode.toDataURL(qrCodeData, {
        width: 300, // Increased size for better scanning
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'H' // Highest error correction
      });
      console.log('QR code generated successfully, data URL length:', qrCodeDataUrl.length);

      // Load QR code image
      const qrImage = new Image();
      await new Promise((resolve, reject) => {
        qrImage.onload = resolve;
        qrImage.onerror = (error) => {
          console.error('Failed to load QR code image:', error);
          reject(error);
        };
        qrImage.src = qrCodeDataUrl;
      });

      // Calculate QR code size and position for optimal visibility
      const qrSize = Math.min(canvas.width * 0.25, canvas.height * 0.25, 200); // Smaller but more visible
      const padding = 30;
      
      // Position QR code in top-right corner to avoid text overlap
      const qrX = canvas.width - qrSize - padding;
      const qrY = padding;

      console.log('QR Code positioning:', { 
        canvasSize: `${canvas.width}x${canvas.height}`, 
        qrSize, 
        qrX, 
        qrY,
        willFit: qrX >= 0 && qrY >= 0 && qrX + qrSize <= canvas.width && qrY + qrSize <= canvas.height
      });

      // Create a much more prominent background for better contrast
      const cardPadding = 20;
      const cardWidth = qrSize + (cardPadding * 2);
      const cardHeight = qrSize + 60; // More space for text
      const cardX = qrX - cardPadding;
      const cardY = qrY - 15;

      // Add strong drop shadow for depth
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillRect(cardX + 4, cardY + 4, cardWidth, cardHeight);

      // Add solid white background for maximum contrast
      ctx.fillStyle = 'rgba(255, 255, 255, 1.0)';
      ctx.fillRect(cardX, cardY, cardWidth, cardHeight);

      // Add thick border for definition
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
      ctx.lineWidth = 2;
      ctx.strokeRect(cardX, cardY, cardWidth, cardHeight);

      // Add "Scan to Join" text with better styling
      ctx.fillStyle = '#1e293b';
      ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'center';
      const centerX = qrX + qrSize / 2;
      ctx.fillText('Scan to Join Event', centerX, qrY - 10);

      // Draw the QR code with error handling
      console.log('Drawing QR code at:', qrX, qrY, 'size:', qrSize);
      ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

      // Add event name below QR code
      if (eventName) {
        ctx.fillStyle = '#475569';
        ctx.font = 'bold 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.textAlign = 'center';
        const truncatedName = eventName.length > 25 ? eventName.substring(0, 25) + '...' : eventName;
        ctx.fillText(truncatedName, centerX, qrY + qrSize + 15);
      }

      // Add comprehensive event details overlay
      const leftPadding = 40;
      const textStartY = canvas.height - 350;
      let currentY = textStartY;
      const lineHeight = 50;

      // Create semi-transparent background for text
      const textAreaWidth = Math.min(canvas.width - qrSize - 150, 650);
      const textAreaHeight = 320;
      
      // Add background with rounded corners effect
      ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
      ctx.fillRect(leftPadding - 25, textStartY - 50, textAreaWidth, textAreaHeight);
      
      // Add subtle border
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.lineWidth = 1;
      ctx.strokeRect(leftPadding - 25, textStartY - 50, textAreaWidth, textAreaHeight);

      // Event title (largest)
      if (eventName) {
        ctx.font = 'bold 40px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.fillStyle = '#FFFFFF';
        ctx.textAlign = 'left';
        
        // Handle long event names with wrapping
        const maxWidth = textAreaWidth - 50;
        const words = eventName.split(' ');
        let line = '';
        
        for (let i = 0; i < words.length; i++) {
          const testLine = line + words[i] + ' ';
          const metrics = ctx.measureText(testLine);
          
          if (metrics.width > maxWidth && i > 0) {
            ctx.fillText(line.trim(), leftPadding, currentY);
            line = words[i] + ' ';
            currentY += lineHeight;
          } else {
            line = testLine;
          }
        }
        ctx.fillText(line.trim(), leftPadding, currentY);
        currentY += lineHeight + 20;
      }

      // Event details with icons
      const details = [];
      
      if (eventType === 'rsvp') {
        if (eventDate) details.push(`📅 ${formatDate(eventDate)}`);
        if (eventTime) details.push(`🕐 ${formatTime(eventTime)}`);
        if (eventLocation) details.push(`📍 ${eventLocation}`);
      } else if (eventType === 'voting' && votingOptions) {
        details.push(`🗳️ Vote on ${votingOptions.length} options`);
        votingOptions.slice(0, 2).forEach(option => {
          const icon = option.type === 'date' ? '📅' : option.type === 'time' ? '🕐' : '📍';
          const value = option.type === 'date' ? formatDate(option.value) : 
                       option.type === 'time' ? formatTime(option.value) : option.value;
          details.push(`${icon} ${value}`);
        });
        if (votingOptions.length > 2) {
          details.push(`   ... and ${votingOptions.length - 2} more`);
        }
      }
      
      if (hostName) details.push(`👤 Hosted by ${hostName}`);
      if (deadline) {
        const deadlineDate = new Date(deadline);
        details.push(`⏰ Deadline: ${deadlineDate.toLocaleDateString()}`);
      }

      // Render details
      details.forEach((detail) => {
        ctx.font = 'bold 24px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.fillStyle = '#FFFFFF';
        ctx.textAlign = 'left';
        
        // Handle long text wrapping
        const maxWidth = textAreaWidth - 50;
        const words = detail.split(' ');
        let line = '';
        
        for (let i = 0; i < words.length; i++) {
          const testLine = line + words[i] + ' ';
          const metrics = ctx.measureText(testLine);
          
          if (metrics.width > maxWidth && i > 0) {
            ctx.fillText(line.trim(), leftPadding, currentY);
            line = words[i] + ' ';
            currentY += lineHeight;
          } else {
            line = testLine;
          }
        }
        ctx.fillText(line.trim(), leftPadding, currentY);
        currentY += lineHeight;
      });

      // Add call-to-action
      if (details.length > 0) {
        currentY += 20;
        ctx.font = 'italic 22px Georgia, serif';
        ctx.fillStyle = '#FFD700';
        ctx.fillText("You're Invited!", leftPadding, currentY);
      }

      console.log('Canvas composition completed, converting to blob...');

      // Convert canvas to blob with high quality
      canvas.toBlob((blob) => {
        if (blob) {
          const compositeImageUrl = URL.createObjectURL(blob);
          console.log('QR code composition completed successfully');
          console.log('Final image size:', blob.size, 'bytes');
          console.log('QR code URL in image:', qrCodeData);
          onCompositeReady(compositeImageUrl);
        } else {
          throw new Error('Failed to create blob from canvas');
        }
      }, 'image/png', 1.0);

    } catch (error) {
      console.error('Error composing image with QR code:', error);
      // Still call the callback even if composition fails
      console.log('QR composition failed, proceeding with original image');
      onCompositeReady(imageUrl);
    }
  };

  // Start compositing when component mounts
  React.useEffect(() => {
    if (imageUrl && qrCodeData) {
      console.log('=== QR CODE COMPOSITION TRIGGER ===');
      console.log('Starting QR code composition with validated data:', {
        imageUrl: !!imageUrl,
        qrCodeData,
        eventName,
        eventType,
        hasEventDetails: !!(eventDate || eventTime || eventLocation || votingOptions?.length),
        votingOptionsCount: votingOptions?.length || 0,
        eventDate,
        eventTime,
        eventLocation
      });
      
      // Add a small delay to ensure all props are ready
      setTimeout(() => {
        composeImageWithQR();
      }, 100);
    } else {
      console.error('=== QR CODE COMPOSITION SKIPPED ===');
      console.error('Missing required props for QR code composition:', { 
        imageUrl: !!imageUrl, 
        qrCodeData: !!qrCodeData,
        imageUrlLength: imageUrl?.length || 0,
        qrCodeDataLength: qrCodeData?.length || 0
      });
    }
  }, [imageUrl, qrCodeData, eventName, eventDate, eventTime, eventLocation, eventType, votingOptions]);

  return null; // This component doesn't render anything visible
};
